import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import Layout from "../../components/Layout/Layout";
import Sidebar from "../../components/Dashboard/Sidebar";
import { useAuth } from "../../context/auth";
import moment from "moment";
import { Select } from "antd";
import { FiSearch, FiChevronDown } from "react-icons/fi";

const { Option } = Select;

const AdminOrders = () => {
  const [statusOptions] = useState([
    "Not Process",
    "Processing",
    "Shipped",
    "Delivered",
    "Cancel",
  ]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [auth] = useAuth();
  const [orderModal, setOrderModal] = useState({
    open: false,
    order: null,
  });

  const [addressModal, setAddressModal] = useState({
    open: false,
    address: "",
  });

  // Filters & sorting
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [paymentFilter, setPaymentFilter] = useState("All");
  const [sortBy, setSortBy] = useState("date-desc");

  const getOrders = async () => {
    try {
      const { data } = await axios.get("/api/v1/auth/all-orders");
      setOrders(data);
      setFilteredOrders(data);
    } catch {
      toast.error("Failed to fetch orders");
    }
  };

  useEffect(() => {
    if (auth?.token) getOrders();
  }, [auth?.token]);

  // Filter + Sorting Logic
  useEffect(() => {
    let result = [...orders];

    if (searchTerm) {
      result = result.filter(
        (order) =>
          order.buyer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.buyer?.address
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          order._id.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    if (statusFilter !== "All") {
      result = result.filter((order) => order.status === statusFilter);
    }

    if (paymentFilter !== "All") {
      if (paymentFilter === "Success") {
        result = result.filter((order) => order.payment.success);
      } else if (paymentFilter === "Failed") {
        result = result.filter((order) => !order.payment.success);
      } else if (paymentFilter === "COD") {
        result = result.filter((order) => order.payment.mode === "COD");
      } else if (paymentFilter === "Online") {
        result = result.filter((order) => order.payment.mode === "Online");
      }
    }

    if (sortBy === "date-desc") {
      result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (sortBy === "date-asc") {
      result.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    } else if (sortBy === "items-high") {
      result.sort((a, b) => b.products.length - a.products.length);
    } else if (sortBy === "items-low") {
      result.sort((a, b) => a.products.length - b.products.length);
    }

    setFilteredOrders(result);
  }, [searchTerm, statusFilter, paymentFilter, sortBy, orders]);

  const handleChange = async (orderId, newStatus) => {
    try {
      await axios.put(`/api/v1/auth/order-status/${orderId}`, {
        status: newStatus,
      });
      toast.success("Order status updated");
      getOrders();
    } catch {
      toast.error("Failed to update order status");
    }
  };

  const getStatusCount = (status) => {
    if (status === "All") return orders.length;
    return orders.filter((order) => order.status === status).length;
  };

  return (
    <Layout title="All Orders">
      <div className="min-h-screen bg-gray-50 mt-20">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">
                All Orders
              </h1>
              <p className="text-gray-600 mt-1.5">
                Manage and track customer orders
              </p>
            </div>
            {/* Menu Toggle Button Removed */}
          </div>

          {/* Layout: Sidebar + Content */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar */}
            <aside className="w-full lg:w-72 flex-shrink-0">
              <Sidebar
                auth={auth}
                activeTab="orders"
                setActiveTab={() => {}}
                onLogout={() => {
                  localStorage.removeItem("auth");
                  window.location.reload();
                }}
              />
            </aside>

            {/* Main Content */}
            <main className="flex-1 min-w-0">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white p-5 rounded-lg border border-gray-200">
                  <div className="text-xs text-gray-500 mb-1">Total orders</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {orders.length}
                  </div>
                </div>
                <div className="bg-white p-5 rounded-lg border border-gray-200">
                  <div className="text-xs text-gray-500 mb-1">Delivered</div>
                  <div className="text-2xl font-bold text-green-600">
                    {getStatusCount("Delivered")}
                  </div>
                </div>
                <div className="bg-white p-5 rounded-lg border border-gray-200">
                  <div className="text-xs text-gray-500 mb-1">Processing</div>
                  <div className="text-2xl font-bold text-amber-600">
                    {getStatusCount("Processing")}
                  </div>
                </div>
                <div className="bg-white p-5 rounded-lg border border-gray-200">
                  <div className="text-xs text-gray-500 mb-1">Cancelled</div>
                  <div className="text-2xl font-bold text-red-600">
                    {getStatusCount("Cancel")}
                  </div>
                </div>
              </div>

              {/* Filters */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                <div className="flex gap-4 mb-6 border-b pb-4 overflow-x-auto">
                  {["All", ...statusOptions].map((status) => (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(status)}
                      className={`px-4 py-2 text-sm font-medium transition ${
                        statusFilter === status
                          ? "text-gray-900 border-b-2 border-gray-900"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>

                <div className="flex flex-wrap gap-4 items-center">
                  {/* Search */}
                  <div className="flex-1 min-w-[260px] relative">
                    <FiSearch
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      size={18}
                    />
                    <input
                      type="text"
                      placeholder="Search by buyer, address, or order ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-gray-900 text-sm"
                    />
                  </div>

                  {/* Payment Filter */}
                  <div className="relative">
                    <select
                      value={paymentFilter}
                      onChange={(e) => setPaymentFilter(e.target.value)}
                      className="appearance-none pl-4 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-gray-900 text-sm bg-white"
                    >
                      <option value="All">All Payments</option>
                      <option value="Success">Success</option>
                      <option value="Failed">Failed</option>
                      <option value="COD">COD</option>
                      <option value="Online">Online</option>
                    </select>
                    <FiChevronDown
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                      size={18}
                    />
                  </div>

                  {/* Sort */}
                  <div className="relative">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="appearance-none pl-4 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-gray-900 text-sm bg-white"
                    >
                      <option value="date-desc">Newest First</option>
                      <option value="date-asc">Oldest First</option>
                      <option value="items-high">Most Items</option>
                      <option value="items-low">Least Items</option>
                    </select>
                    <FiChevronDown
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                      size={18}
                    />
                  </div>

                  {/* Results */}
                  <div className="text-sm text-gray-600">
                    Showing{" "}
                    <span className="font-semibold text-gray-800">
                      {filteredOrders.length}
                    </span>{" "}
                    orders
                  </div>
                </div>
              </div>

              {/* Orders Table */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {[
                          "Order",
                          "Customer",
                          "Mobile",
                          "Date",
                          "Total",
                          "Payment",
                          "Items",
                          "Status",
                        ].map((head) => (
                          <th
                            key={head}
                            className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide"
                          >
                            {head}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredOrders.length === 0 ? (
                        <tr>
                          <td
                            colSpan="8"
                            className="px-4 py-10 text-center text-gray-500"
                          >
                            No orders found
                          </td>
                        </tr>
                      ) : (
                        filteredOrders.map((order, index) => (
                          <tr key={order._id} className="hover:bg-gray-50 ">
                            {/* Order Column */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <img
                                  src={`/${order.products[0]?.product?.photos[0]}`}
                                  onClick={() =>
                                    setOrderModal({ open: true, order })
                                  }
                                  className="w-10 h-10 cursor-pointer transition rounded object-cover border"
                                  alt="Product"
                                />
                                <div>
                                  <div
                                    onClick={() =>
                                      setOrderModal({ open: true, order })
                                    }
                                    className="text-sm cursor-pointer transition font-medium text-gray-900"
                                  >
                                    {order.products[0]?.product?.name}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    #{index + 1}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Customer */}
                            <td className="px-4 py-3">
                              <div className="text-sm font-medium text-gray-900">
                                {order.buyer?.name || "N/A"}
                              </div>

                              {order.buyer?.address && (
                                <button
                                  onClick={() =>
                                    setAddressModal({
                                      open: true,
                                      address: order.buyer.address,
                                    })
                                  }
                                  className="text-blue-600 hover:underline text-xs mt-0.5"
                                >
                                  Address
                                </button>
                              )}
                            </td>

                            {/* Mobile */}
                            <td className="px-4 py-3 text-sm text-gray-800">
                              {order.buyer?.phone ||
                                order.buyer?.mobile ||
                                "N/A"}
                            </td>

                            {/* Date */}
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {moment(order.createdAt).format(
                                "MMM DD, hh:mm A",
                              )}
                            </td>

                            {/* Total */}
                            <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                              ₹{order.finalAmount?.toLocaleString()}
                            </td>

                            {/* Payment */}
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                                  order.payment?.success
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-700"
                                }`}
                              >
                                {order.payment?.mode}
                              </span>
                            </td>

                            {/* Items */}
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {order.products.length}{" "}
                              {order.products.length === 1 ? "item" : "items"}
                            </td>

                            {/* Status */}
                            <td className="px-4 py-3 text-sm text-gray-700">
                              <Select
                                bordered={false}
                                style={{ minWidth: 120 }}
                                className="font-medium"
                                defaultValue={order.status}
                                onChange={(value) =>
                                  handleChange(order._id, value)
                                }
                              >
                                {statusOptions.map((s) => (
                                  <Option key={s} value={s}>
                                    {s}
                                  </Option>
                                ))}
                              </Select>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>

      {/* Address Modal */}
      {addressModal.open && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-md rounded-lg p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              Full Address
            </h2>
            <p className="text-gray-700 whitespace-pre-line">
              {addressModal.address}
            </p>

            <button
              onClick={() => setAddressModal({ open: false, address: "" })}
              className="mt-6 w-full py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
            >
              Close
            </button>
          </div>
        </div>
      )}
      {orderModal.open && orderModal.order && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <h2 className="text-xl font-bold">Order Details</h2>
                <p className="text-sm text-gray-500">#{orderModal.order._id}</p>
              </div>

              <button
                onClick={() =>
                  setOrderModal({
                    open: false,
                    order: null,
                  })
                }
                className="text-2xl"
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {/* Customer Information */}
                <div className="bg-white border rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-semibold border-b pb-3 mb-5">
                    Customer Information
                  </h3>

                  <div className="space-y-5">
                    <div className="flex justify-between items-start">
                      <span className="text-sm text-gray-500">
                        Customer Name
                      </span>
                      <span className="font-semibold text-gray-900 text-right">
                        {orderModal.order.buyer?.name || "N/A"}
                      </span>
                    </div>

                    <div className="flex justify-between items-start">
                      <span className="text-sm text-gray-500">
                        Phone Number
                      </span>
                      <span className="font-medium text-gray-900">
                        {orderModal.order.buyer?.phone || "N/A"}
                      </span>
                    </div>

                    <div className="flex justify-between items-start gap-6">
                      <span className="text-sm text-gray-500 whitespace-nowrap">
                        Delivery Address
                      </span>

                      <span className="font-medium text-gray-900 text-right">
                        {orderModal.order.buyer?.address || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Order Information */}
                <div className="bg-white border rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-semibold border-b pb-3 mb-5">
                    Order Information
                  </h3>

                  <div className="space-y-5">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">
                        Order Status
                      </span>

                      <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-medium">
                        {orderModal.order.status}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">
                        Payment Method
                      </span>

                      <span className="font-medium text-gray-900">
                        {orderModal.order.payment?.mode === "COD"
                          ? "Cash on Delivery"
                          : "Online"}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">
                        Payment Status
                      </span>

                      {orderModal.order.payment?.mode === "COD" ? (
                        <span className="px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 text-sm font-medium">
                          Pending
                        </span>
                      ) : orderModal.order.payment?.success ? (
                        <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-medium">
                          Success
                        </span>
                      ) : (
                        <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 text-sm font-medium">
                          Failed
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <h3 className="font-semibold mb-4">Products</h3>

              {/* Desktop Layout */}
              <div className="hidden lg:block">
                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                  {/* Table Header */}
                  <div className="grid grid-cols-12 items-center bg-gray-50 border-b px-6 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                    <div className="col-span-5">Product</div>
                    <div className="col-span-1 text-center">Color</div>
                    <div className="col-span-1 text-center">Size</div>
                    <div className="col-span-1 text-center">Qty</div>
                    <div className="col-span-2 text-right">Price</div>
                    <div className="col-span-2 text-right">Total</div>
                  </div>

                  {/* Products */}
                  {orderModal.order.products.map((item, i) => {
                    const selectedColor = item.product?.colors?.find(
                      (c) => String(c._id) === String(item.color),
                    );

                    return (
                      <div
                        key={i}
                        className="grid grid-cols-12 items-center gap-4 border-b border-gray-100 px-6 py-5 hover:bg-gray-50 transition"
                      >
                        {/* Product */}
                        <div className="col-span-5 flex items-center gap-4 min-w-0">
                          <div className="h-24 w-20 flex-shrink-0 overflow-hidden rounded-xl border bg-gray-100">
                            <img
                              src={`/${item.product?.photos?.[0]}`}
                              alt={item.product?.name}
                              className="h-full w-full object-contain"
                            />
                          </div>

                          <div className="min-w-0 flex-1">
                            <h3 className="truncate text-base font-semibold text-gray-900">
                              {item.product?.name}
                            </h3>

                            <p className="truncate text-sm text-gray-500">
                              {item.product?.category?.name || "Uncategorized"}
                            </p>
                          </div>
                        </div>

                        {/* Color */}
                        <div className="col-span-1 flex justify-center">
                          {selectedColor ? (
                            <div className="flex flex-col items-center gap-1">
                              <span
                                className="h-5 w-5 rounded-full border border-gray-300"
                                style={{ backgroundColor: selectedColor.hex }}
                              />
                              <span className="max-w-[60px] truncate text-xs text-gray-700">
                                {selectedColor.name}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>

                        {/* Size */}
                        <div className="col-span-1 flex justify-center">
                          <span className="min-w-[48px] rounded-full bg-gray-100 px-3 py-1 text-center text-sm font-medium">
                            {item.size || "-"}
                          </span>
                        </div>

                        {/* Quantity */}
                        <div className="col-span-1 text-center">
                          <span className="text-lg font-semibold text-gray-900">
                            {item.quantity}
                          </span>
                        </div>

                        {/* Price */}
                        <div className="col-span-2 text-right">
                          <span className="font-semibold tabular-nums text-gray-800">
                            ₹{item.product.price.toLocaleString()}
                          </span>
                        </div>

                        {/* Total */}
                        <div className="col-span-2 text-right">
                          <span className="text-lg font-bold text-green-700 tabular-nums">
                            ₹
                            {(
                              item.product.price * item.quantity
                            ).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    );
                  })}

                  {/* Footer */}
                  <div className="border-t bg-gray-50 px-8 py-6">
                    <div className="w-full">
                      <div className="grid grid-cols-2 gap-y-4 text-base">
                        <span className="text-gray-600 font-medium">
                          Total Items
                        </span>
                        <span className="text-right font-semibold tabular-nums">
                          {orderModal.order.totalQuantity}
                        </span>

                        <span className="text-gray-600 font-medium">
                          Subtotal
                        </span>
                        <span className="text-right font-semibold tabular-nums">
                          ₹{orderModal.order.subtotal?.toLocaleString()}
                        </span>

                        <span className="text-green-600 font-medium">
                          Discount
                        </span>
                        <span className="text-right font-semibold text-green-600 tabular-nums">
                          -₹{orderModal.order.discountAmount?.toLocaleString()}
                        </span>

                        <span className="text-gray-600 font-medium">
                          Shipping
                        </span>
                        <span className="text-right font-semibold">FREE</span>
                      </div>

                      <div className="mt-5 border-t pt-5 flex items-center justify-between">
                        <span className="text-2xl font-bold text-gray-900">
                          Grand Total
                        </span>

                        <span className="text-3xl font-bold text-green-700 tabular-nums">
                          ₹{orderModal.order.finalAmount?.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Layout */}
              <div className="lg:hidden">
                <div className="flex gap-5 overflow-x-auto pb-3 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                  {orderModal.order.products.map((item, i) => {
                    const selectedColor = item.product?.colors?.find(
                      (c) => String(c._id) === String(item.color),
                    );

                    return (
                      <div
                        key={i}
                        className="snap-start flex-shrink-0 w-[320px] bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden"
                      >
                        <div className="bg-gray-50 flex items-center justify-center p-3">
                          <img
                            src={`/${item.product?.photos?.[0]}`}
                            alt={item.product?.name}
                            className="w-full h-44 object-contain"
                          />
                        </div>

                        <div className="p-4">
                          <h3 className="font-semibold text-lg truncate">
                            {item.product?.name}
                          </h3>

                          <div className="flex items-center gap-2 mt-3">
                            {selectedColor && (
                              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-xs">
                                <span
                                  className="w-3 h-3 rounded-full border"
                                  style={{ background: selectedColor.hex }}
                                />
                                {selectedColor.name}
                              </span>
                            )}

                            {item.size && (
                              <span className="px-3 py-1 rounded-full bg-gray-100 text-xs">
                                {item.size}
                              </span>
                            )}
                          </div>

                          <div className="mt-5 flex items-end justify-between">
                            <div className="flex flex-col items-center justify-center text-center">
                              <span className="text-xs uppercase tracking-wide text-gray-400">
                                Quantity
                              </span>

                              <span className="mt-1 text-xl font-bold text-gray-900 leading-none">
                                {item.quantity}
                              </span>
                            </div>

                            <div className="flex flex-col items-end">
                              <span className="text-xs uppercase tracking-wide text-gray-400">
                                Item Total
                              </span>

                              <span className="mt-1 text-xl font-bold text-green-700 leading-none">
                                ₹
                                {(
                                  item.product.price * item.quantity
                                ).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Order Summary */}
                <div className="mt-8">
                  <div className="w-full rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                    <div className="border-b bg-gray-50 px-6 py-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Order Summary
                      </h3>
                    </div>

                    <div className="px-6 py-6">
                      <div className="ml-auto w-full max-w-[420px] space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Total Quantity</span>
                          <span className="font-semibold text-gray-900">
                            {orderModal.order.totalQuantity}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Subtotal</span>
                          <span className="font-semibold tabular-nums">
                            ₹{orderModal.order.subtotal?.toLocaleString()}
                          </span>
                        </div>

                        <div className="flex items-center justify-between text-green-600">
                          <span>Discount</span>
                          <span className="font-semibold tabular-nums">
                            -₹
                            {orderModal.order.discountAmount?.toLocaleString()}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Shipping</span>
                          <span className="font-semibold">FREE</span>
                        </div>

                        <div className="border-t pt-4 flex items-center justify-between">
                          <span className="text-xl font-bold text-gray-900">
                            Amount Paid
                          </span>

                          <span className="text-2xl font-bold text-green-700 tabular-nums">
                            ₹{orderModal.order.finalAmount?.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminOrders;
